
import { Textarea } from "@/components/ui/textarea";
import { useEffect, useState } from "react";

type CourseFooterTextFieldProps = {
  footerText: any;
  onChange: (value: any) => void;
  isEditing: boolean;
};

export function CourseFooterTextField({ 
  footerText, 
  onChange, 
  isEditing 
}: CourseFooterTextFieldProps) {
  // Store the formatted footer text as a string for editing
  const [footerTextString, setFooterTextString] = useState<string>("");

  // Format JSON data for display or editing
  const formatJsonForDisplay = (jsonData: any) => {
    if (!jsonData) return '';
    return typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData, null, 2);
  };

  // Set up footer text string when component mounts or footerText changes
  useEffect(() => {
    console.log('Footer text received:', footerText);
    setFooterTextString(formatJsonForDisplay(footerText));
  }, [footerText]);
  
  // Handle footer text change
  const handleFooterTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setFooterTextString(value);
    onChange(value);
  };

  // Render footer text items based on their format
  // Safe to use dangerouslySetInnerHTML as content is admin-controlled
  const renderFooterText = () => {
    if (!footerText) return <div>No footer text</div>;
    
    try {
      // Handle already parsed object
      if (typeof footerText === 'object') {
        // Check if it's a language-keyed object (e.g., {en: "...", es: "..."})
        if (footerText.en && typeof footerText.en === 'string') {
          return (
            <div dangerouslySetInnerHTML={{ __html: footerText.en }} />
          );
        }
        
        // Handle array of items
        if (Array.isArray(footerText)) {
          return (
            <ul className="list-disc pl-5 space-y-1">
              {footerText.map((item, index) => (
                <li 
                  key={index}
                  dangerouslySetInnerHTML={{ 
                    __html: typeof item === 'string' ? item : JSON.stringify(item)
                  }}
                />
              ))}
            </ul>
          );
        }
        
        // Fallback for other object types
        return <div className="whitespace-pre-wrap">{JSON.stringify(footerText, null, 2)}</div>;
      }
      
      // Handle string that needs parsing
      if (typeof footerText === 'string' && footerText.trim()) {
        const parsedFooterText = JSON.parse(footerText);
        
        // Check if parsed object has language keys
        if (parsedFooterText.en && typeof parsedFooterText.en === 'string') {
          return (
            <div dangerouslySetInnerHTML={{ __html: parsedFooterText.en }} />
          );
        }
        
        // Handle parsed array
        if (Array.isArray(parsedFooterText)) {
          return (
            <ul className="list-disc pl-5 space-y-1">
              {parsedFooterText.map((item, index) => (
                <li 
                  key={index}
                  dangerouslySetInnerHTML={{ 
                    __html: typeof item === 'string' ? item : JSON.stringify(item)
                  }}
                />
              ))}
            </ul>
          );
        }
        
        // Fallback for other parsed types
        return <div className="whitespace-pre-wrap">{JSON.stringify(parsedFooterText, null, 2)}</div>;
      }
      
      // Fallback
      return <div>{String(footerText)}</div>;
    } catch (error) {
      console.error("Error rendering footer text:", error);
      return <div>Error displaying footer text: {String(error)}</div>;
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Footer Text</label>
      {isEditing ? (
        <div>
          <Textarea
            value={footerTextString}
            onChange={handleFooterTextChange}
            rows={4}
            placeholder='["<b>Bold text</b>", "Normal text"] or {"en": "<b>No hidden fees!</b> Details here"}'
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Enter footer text as a JSON array or object.
          </p>
        </div>
      ) : (
        <div className="whitespace-pre-wrap">
          {renderFooterText()}
        </div>
      )}
    </div>
  );
}
